import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Link, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { ChevronLeft, ChevronDown } from "lucide-react";

import { getAccessToken } from "../../../api/auth";
import { API } from "../../../api";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import { cn } from "@/Components/ui/utils";
import { Button } from "../../../Components/Common/Tailwind";
import { filterUsablePasses, sortByExpiryAsc } from "./passUsability.mjs";

export default function AmazingpassOrderScreen() {
    const navigate = useNavigate();
    const location = useLocation();

    const {
        product,
        selectDate,
        spotId = [],
        usablePasses,
    } = location.state || {};

    const accessToken = getAccessToken();

    // "목록으로"/"이전"은 직전 /amazingpass 조회 리스트로 복귀한다.
    // Calendar는 ?date= 파라미터가 있으면 해당 날짜의 조회 리스트(STEP_PRODUCT)로 초기화된다.
    const backToListPath = selectDate ? `/amazingpass?date=${selectDate}` : "/amazingpass";

    // 좌석에 배정할 "사용 가능한 패스"를 만료 임박 순으로 보관.
    // calendar 에서 넘겨주면 그대로 쓰고, 없으면 여기서 재조회한다.
    const [passes, setPasses] = useState(() =>
        Array.isArray(usablePasses) ? sortByExpiryAsc(usablePasses) : []
    );
    const [loading, setLoading] = useState(() => !Array.isArray(usablePasses));
    const [submitting, setSubmitting] = useState(false);
    const [seatsOpen, setSeatsOpen] = useState(true);

    useEffect(() => {
        if (Array.isArray(usablePasses)) return undefined;
        if (!product) return undefined;

        let cancelled = false;
        const loadPasses = async () => {
            setLoading(true);
            try {
                const collected = [];
                let page = 1;
                for (let i = 0; i < 50; i++) {
                    const response = await API.get(`/precoupon/user/?page=${page}&state=사용안함`, {
                        headers: { Authorization: `Bearer ${accessToken}` },
                    });
                    const results = response.data?.results || [];
                    collected.push(...results);
                    const next = response.data?.next;
                    const match = next ? String(next).match(/page=(\d+)/) : null;
                    if (!match) break;
                    page = Number(match[1]);
                }
                if (!cancelled) {
                    setPasses(sortByExpiryAsc(filterUsablePasses(collected, { product, selectDate })));
                }
            } catch (error) {
                console.error("보유 패스 조회 실패", error);
                if (!cancelled) setPasses([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadPasses();
        return () => {
            cancelled = true;
        };
    }, [usablePasses, product, selectDate, accessToken]);

    // 좌석은 자리 번호 오름차순(작은 번호 먼저)으로 정렬해 표시/배정 순서를 통일한다.
    // numeric 옵션으로 "2" < "16" 같은 자연 정렬 처리.
    const sortedSpotId = useMemo(
        () =>
            [...spotId].sort((a, b) =>
                String(a.name).localeCompare(String(b.name), undefined, { numeric: true, sensitivity: "base" })
            ),
        [spotId]
    );

    // 좌석 ↔ 패스 자동 배정 (만료 임박 순)
    const assignments = useMemo(
        () => sortedSpotId.map((seat, idx) => ({ seat, pass: passes[idx] || null })),
        [sortedSpotId, passes]
    );

    const exceedsPassLimit = spotId.length > passes.length;
    // 이번 예약으로 소진되는 패스 수(좌석 1 = 패스 1)
    const usedPassCount = spotId.length;

    // 예약 날짜를 "2026.07.10 (금)" 형태(요일 포함)로 표시
    const dateLabel = useMemo(() => {
        if (!selectDate) return "";
        const m = moment(selectDate);
        const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
        return `${m.format("YYYY.MM.DD")} (${weekdays[m.day()]})`;
    }, [selectDate]);

    const processAmazingPass = async () => {
        if (!spotId.length) {
            toast.warning("선택된 좌석이 없습니다.");
            return;
        }
        // 결제 단계 검증: 좌석 수가 사용 가능한 패스 갯수를 초과하면 진행 불가
        if (exceedsPassLimit) {
            toast.error(
                `선택한 좌석 수(${spotId.length}개)가 사용 가능한 패스 갯수(${passes.length}개)를 초과합니다.`
            );
            return;
        }

        setSubmitting(true);
        try {
            for (let i = 0; i < assignments.length; i++) {
                const { seat, pass } = assignments[i];
                await API.put(
                    `/precoupon/use-coupon/${pass.id}/`,
                    {
                        user_coupon_id: pass.id,
                        spot_id: seat.id,
                        start_date: selectDate,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );
            }
            toast.success("예약이 완료되었습니다.");
            navigate("/my-page", { replace: true });
        } catch (error) {
            console.error("어메이징패스 예약 실패", error);
            toast.error(error?.response?.data?.error || "예약 처리 중 오류가 발생했습니다.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!product || !spotId.length) {
        return (
            <MobileViewContainer>
                <div className="flex flex-col items-center justify-center gap-4" style={{ minHeight: "70vh" }}>
                    <p className="text-sm text-gray-500 font-[Gmarket-M]">예약 정보가 없습니다.</p>
                    <Button className="bg-black text-white" onClick={() => navigate("/amazingpass", { replace: true })}>
                        패스로 예약하기
                    </Button>
                </div>
            </MobileViewContainer>
        );
    }

    return (
        <React.Fragment>
            <MobileViewContainer>
                {/* 고정 헤더: 스크롤해도 상단에 남고 blur로 본문과 분리 (/amazingpass 헤더와 통일) */}
                <header className="fixed top-0 left-1/2 z-50 flex h-16 w-full max-w-160 -translate-x-1/2 items-center justify-center border-b border-gray-200/60 bg-white/80 px-4 backdrop-blur-md">
                    <Link to={backToListPath} aria-label="목록으로" className="absolute left-4 inline-flex items-center text-black no-underline transition-opacity duration-150 active:opacity-60">
                        <ChevronLeft size={24} className="shrink-0" />
                    </Link>
                    <h1 className="m-0 text-lg font-bold text-gray-900 font-[Gmarket-B]">패스 예약 확인</h1>
                </header>
                <div className="h-16" aria-hidden />

                <div className="py-2 bg-gray-100" style={{ minHeight: "100vh" }}>
                    <div className="mt-6 flex flex-col items-center gap-1.5 text-center animate-fade-up">
                        <div className="text-[20px] text-gray-900 font-[Gmarket-B] text-balance break-keep">{product.name}</div>
                        <div className="text-sm text-gray-700 font-[Gmarket-M] tabular-nums">{dateLabel}</div>
                    </div>

                    {/* 예약 요약 영수증: 선택 좌석(요약) / 사용 패스(합계). 자리 세부는 하단 리스트에서 표시 */}
                    <div className="mt-5 px-4 animate-fade-up" style={{ animationDelay: "80ms" }}>
                        <div className="relative rounded-t-3xl bg-white shadow-[0_2px_20px_rgba(0,0,0,0.05)]">
                            <div className="p-5">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-sm font-bold text-gray-900 font-[Gmarket-M]">선택 좌석</span>
                                    <span className="text-sm font-bold text-gray-900 font-[Gmarket-M] tabular-nums">{spotId.length}개</span>
                                </div>
                            </div>

                            {/* 절취선 노치 (좌우 원형 컷아웃 + 점선) */}
                            <div className="relative">
                                <div className="absolute -left-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-gray-100"></div>
                                <div className="absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-gray-100"></div>
                                <div className="mx-4 border-t-2 border-dashed border-gray-200"></div>
                            </div>

                            {/* 합계 영역: 이번 예약으로 사용(차감)되는 패스 */}
                            <div className="flex items-center justify-between gap-3 px-5 pb-4 pt-5">
                                <span className="text-sm font-bold text-gray-900 font-[Gmarket-M]">사용 패스</span>
                                {loading ? (
                                    <span className="text-sm text-gray-600 font-[Gmarket-M]">확인 중...</span>
                                ) : (
                                    <span className="flex items-baseline gap-1 font-[Gmarket-B] tabular-nums">
                                        <span className={cn("text-sm font-black leading-none", exceedsPassLimit ? "text-[#dc2626]" : "text-[#f6475f]")}>{usedPassCount}</span>
                                        <span className="text-sm font-bold text-gray-900">/ 보유 {passes.length}</span>
                                    </span>
                                )}
                            </div>

                            {/* 톱니(zigzag) 하단 엣지 */}
                            <svg className="block w-full" height="10" viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true">
                                <polygon points="0,10 0,0 5,10 10,0 15,10 20,0 25,10 30,0 35,10 40,0 45,10 50,0 55,10 60,0 65,10 70,0 75,10 80,0 85,10 90,0 95,10 100,0 100,10" fill="#f3f4f6" />
                            </svg>
                        </div>
                    </div>

                    {exceedsPassLimit && !loading && (
                        <div className="mt-3 px-4 animate-fade-up" style={{ animationDelay: "160ms" }}>
                            <div className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3">
                                <p className="text-xs leading-relaxed text-[#dc2626] font-bold">
                                    선택한 좌석 수(<span className="tabular-nums">{spotId.length}</span>개)가 사용 가능한 패스 갯수(<span className="tabular-nums">{passes.length}</span>개)를 초과합니다. 좌석을 다시 선택해주세요.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="space-y-4 px-4 pb-40 mt-4">
                        {/* 선택 좌석 드롭다운: 헤더를 누르면 자리별 패스 사용 리스트를 펼침/접기 */}
                        <div className="animate-fade-up" style={{ animationDelay: "240ms" }}>
                            <button
                                type="button"
                                onClick={() => setSeatsOpen((v) => !v)}
                                aria-expanded={seatsOpen}
                                className="flex w-full items-center justify-between gap-3 px-1 py-1 transition-opacity duration-150 active:opacity-60"
                            >
                                <span className="flex items-center gap-1.5 text-sm font-bold text-gray-900 font-[Gmarket-M]">
                                    선택 좌석
                                    <span className="text-[#f6475f] tabular-nums">{spotId.length}개</span>
                                </span>
                                <ChevronDown size={18} strokeWidth={2.5} className={cn("text-gray-900 transition-transform duration-200", seatsOpen && "rotate-180")} />
                            </button>
                            {seatsOpen && (
                                <div className="mt-3 space-y-2 animate-fade-up">
                                    {assignments.map(({ seat, pass }, idx) => (
                                        <div key={idx} className="flex items-center justify-between bg-white rounded-2xl border border-gray-100 px-4 py-3.5 shadow-[0_2px_14px_rgba(0,0,0,0.04)]">
                                            <div className="bg-gray-900 text-white px-2.5 py-1 rounded-lg text-xs font-bold font-[Gmarket-M]">
                                                자리 {seat.name}
                                            </div>
                                            {pass ? (
                                                <span className="rounded-full bg-[#f6475f]/10 px-2.5 py-1 text-xs font-bold text-[#f6475f]">패스 사용</span>
                                            ) : (
                                                <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-bold text-[#dc2626]">패스 부족</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* 만료 임박 패스 순차 사용 안내 (시각적 방해 없이 작은 텍스트로만) */}
                        <p className="px-1 text-[11px] leading-relaxed text-gray-700 font-[Gmarket-M] text-pretty animate-fade-up" style={{ animationDelay: "300ms" }}>
                            만료기한이 임박한 패스권부터 순차적으로 사용됩니다.
                        </p>

                        {/* 사용 후 취소 제한 안내 */}
                        <div className="rounded-2xl bg-white border border-gray-100 shadow-[0_2px_14px_rgba(0,0,0,0.04)] px-4 py-3 animate-fade-up" style={{ animationDelay: "320ms" }}>
                            <p className="text-[11px] leading-relaxed text-gray-900 text-pretty">
                                · 예약을 확정하면 위 패스가 <b className="text-gray-900">즉시 차감</b>됩니다.<br />
                                · 패스는 <b className="text-gray-900">사용 후 취소·환불이 제한</b>될 수 있습니다.
                            </p>
                        </div>
                    </div>

                    {/* Sticky Bottom Bar */}
                    <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[640px] z-[100] px-4 pb-8 pt-2 bg-gradient-to-t from-gray-100 via-gray-100 to-transparent">
                        <div className="bg-white rounded-[24px] border border-gray-100 p-4">
                            <Button
                                className={cn(
                                    "w-full py-[18px] text-[17px] font-bold rounded-2xl border-0 font-[Gmarket-M] transition-[background-color,color,scale] duration-200 active:scale-[0.96]",
                                    exceedsPassLimit || submitting || loading
                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                        : "bg-[#f6475f] text-white active:bg-[#e02e48]"
                                )}
                                disabled={exceedsPassLimit || submitting || loading}
                                onClick={processAmazingPass}
                            >
                                {submitting ? "예약 처리 중..." : loading ? "패스 확인 중..." : "예약 확정"}
                            </Button>
                        </div>
                    </div>
                </div>
            </MobileViewContainer>
        </React.Fragment>
    );
}
